import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";
import { CASTLE_ROCK_PROFILE } from "@/lib/cityProfiles/castle-rock";

const CONFIG: IndustryCityMarketingConfig = {
  slug: "plumbing-marketing-castle-rock",
  industry: "Plumbing",
  industryLower: "plumbing",
  ...CASTLE_ROCK_PROFILE,
  metaTitle: "Plumbing Marketing in Castle Rock, CO | SEO, Maps, Ads & Web",
  metaDescription: "Owner-led plumbing marketing for Castle Rock plumbers \u2014 SEO, Google Maps, web design, and ads across The Meadows and Crystal Valley. Flat $1,650/mo, no contracts.",
  h1: { lead: "Plumbing marketing in", gold: "Castle Rock", trail: "\u2014 from The Meadows to Founders Village" },
  eyebrow: "Castle Rock Plumbing Marketing",
  heroDescription: "We help Castle Rock plumbers own the Map Pack across 80104, 80108, and 80109, win 'emergency plumber' and 'water heater replacement' searches, and keep crews booked year-round.",
  intro: "When a pipe bursts in a The Meadows basement at 2 a.m. in January, the homeowner doesn't comparison shop \u2014 they call the first plumber who shows up on their phone. In Castle Rock, that's often a call-center brand or a lead-gen aggregator reselling the job, not a local shop that can actually be there in 45 minutes. Winning Castle Rock plumbing means owning 'emergency plumber near me' in the Map Pack, ranking for high-ticket work like water heater, sewer line, and repipe jobs across Crystal Valley, Founders Village, Castlewood Ranch, and converting panicked mobile callers instantly. That's what we build \u2014 owner-led, flat $1,650/month, no contracts.",
  painPoints: [
  "Paying lead-gen aggregators per call for Castle Rock jobs you should be getting directly from Google.",
  "Emergency calls going to whichever plumber answers first \u2014 and the Map Pack dominated by franchise brands.",
  "Water heater, sewer line, and repipe jobs in Founders Village going to competitors with stronger review counts."
],
  services: {
  "seo": "Rank for 'plumber Castle Rock,' 'emergency plumber near me,' and 'water heater replacement' across 80104, 80108, and 80109.",
  "googleMaps": "Top-3 Map Pack for Castle Rock plumbers \u2014 24/7 hours tuned, service categories dialed in, and steady review velocity.",
  "webDesign": "Hand-coded plumbing sites that load sub-2s on a phone and get a panicked homeowner to tap-to-call in one move.",
  "ppc": "Emergency-focused Google Ads and LSA with call-only bidding that ramps during Castle Rock freeze events.",
  "geoAi": "Get cited when Castle Rock homeowners ask ChatGPT 'who's a reliable plumber near me open now?'",
  "socialMedia": "Trust content \u2014 tech intros, before/after jobs, maintenance tips \u2014 that turns one-time fixes into repeat customers."
},
  trustPoints: [
  "Owner-led from our Colorado Springs HQ \u2014 we know Castle Rock plumbers' market firsthand.",
  "300+ Colorado clients served since 2006 across roofing, plumbing, HVAC, and electrical.",
  "Flat $1,650/month marketing, websites from $1,850 \u2014 published publicly.",
  "Talk to Chris directly. No account managers or offshore handoffs."
],
  testimonial: { quote: "We were paying a lead service for every call and fighting three other plumbers for each one. Now our own Google listing brings the calls \u2014 and they're ours alone.", role: "Plumbing company owner", location: "Castle Rock, CO" },
  faqs: [
  {
    "q": "How do I get more plumbing leads in Castle Rock?",
    "a": "Own the Google Map Pack for 'emergency plumber' and 'plumber near me' across 80104, 80108, and 80109, with a website that converts mobile callers in seconds. Add Google Local Services Ads for pay-per-lead calls and stop renting leads from aggregators."
  },
  {
    "q": "How much does plumbing marketing cost in Castle Rock?",
    "a": "Our Growth Marketing package \u2014 SEO, Google Maps, GEO AI, and social \u2014 is $1,650/month flat. A new plumbing website starts at $1,850 one-time, owned outright, with in-house financing available. Prices are public because Castle Rock plumbers deserve a straight answer."
  },
  {
    "q": "How long until I rank in the Castle Rock Map Pack?",
    "a": "Most Castle Rock plumbing clients see Map Pack movement within 60\u2013120 days, and citywide top-3 across The Meadows, Crystal Valley, Founders Village usually takes 4\u20136 months of consistent work. It compounds month over month."
  },
  {
    "q": "Do you cover Castle Pines, Larkspur, Sedalia, and Parker too?",
    "a": "Yes. We build service-area pages and Map Pack signals for every area your trucks reach, including Castle Pines, Larkspur, Sedalia, and Parker."
  },
  {
    "q": "When should I ramp up plumbing ads in Castle Rock?",
    "a": "Ahead of winter freeze season (December\u2013February). We build the campaigns in advance so they can go live the moment demand spikes, and scale them back when your schedule is full."
  },
  {
    "q": "Are you actually local to Castle Rock?",
    "a": "We're headquartered in Colorado Springs (3210 E Woodmen Rd) and work Castle Rock weekly. Verified Google Business Profile, 4.9\u2605 average, Colorado-owned since 2006."
  },
  {
    "q": "Do you lock me into a contract?",
    "a": "No. Every engagement is month-to-month \u2014 no early-termination fees. We earn the renewal every month."
  }
],
  related: [
  {
    "to": "/plumbing-marketing-colorado",
    "label": "Plumbing marketing across Colorado",
    "description": "Statewide hub for Colorado plumbers."
  },
  {
    "to": "/castle-rock-digital-marketing",
    "label": "Castle Rock digital marketing hub",
    "description": "Every service we offer for Castle Rock businesses."
  },
  {
    "to": "/plumbing-marketing-colorado-springs",
    "label": "Plumbing marketing in Colorado Springs",
    "description": "Same playbook tuned for Colorado Springs plumbers."
  },
  {
    "to": "/plumbing-marketing-parker",
    "label": "Plumbing marketing in Parker",
    "description": "Same playbook tuned for Parker plumbers."
  },
  {
    "to": "/hvac-marketing-castle-rock",
    "label": "HVAC marketing in Castle Rock",
    "description": "Our HVAC playbook for Castle Rock."
  },
  {
    "to": "/pricing",
    "label": "Flat, public pricing",
    "description": "$1,650/mo marketing, websites from $1,850."
  }
],
  showPricing: true,
};

export const Route = createFileRoute("/plumbing-marketing-castle-rock")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
